import React from 'react';
import ChevronRightSmall from '@uber/base-icons/chevron-right-small';
import { useStyletron } from 'baseui';
import { type ListOverrides, ListItem } from 'baseui/list';
import { type TagOverrides, HIERARCHY, SIZE, Tag } from 'baseui/tag';
import type { Theme } from 'baseui/theme';
import { ParagraphMedium } from 'baseui/typography';
import type { StyleObject } from 'styletron-react';
import LabelColorPicker from './label-color-picker';
type PropsT = {
  id: string;
  name: string;
  annotationCount?: number;
  isSelected: boolean;
  endEnhancer?: React.ReactNode;
  nameEnhancer?: React.ReactNode;
  onClick: (name: string) => void;
  onCountClick: (name: string) => void;
  color: string;
  renderIcon: (props: {
    strokeColor: string;
  }) => React.ReactNode;
  handleLabelColorChange: (colorPickerPayload: {
    color: string;
    title: string;
  }) => boolean;
  getListItemOverrides?: (isSelected: boolean) => ListOverrides;
  shortcutKey?: number;
  isM4Enabled?: boolean;
  getTagOverrides?: (isSelected: boolean) => TagOverrides;
  hideCountTag?: boolean;
};
const getAnnotationCountTagOverrides = (): TagOverrides => ({
  Root: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      margin: 0,
      display: 'flex',
      flexDirection: 'row-reverse',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${$theme.sizing.scale100} ${$theme.sizing.scale100} ${$theme.sizing.scale100} ${$theme.sizing.scale200}`,
      backgroundColor: $theme.colors.blue100,
      borderColor: $theme.colors.blue100,
      color: $theme.colors.blue700
    })
  },
  StartEnhancerContainer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      padding: 0,
      paddingLeft: $theme.sizing.scale0
    })
  },
  Text: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      ...$theme.typography.LabelXSmall
    })
  }
});

// ListItem overrides
const getDefaultListItemOverrides = (isSelected: boolean): ListOverrides => ({
  Root: {
    props: {
      'data-testid': 'label-item'
    },
    style: ({
      $theme
    }: {
      $theme: Theme;
    }): StyleObject => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${isSelected ? 'transparent' : $theme.colors.borderOpaque}`,
      padding: $theme.sizing.scale500,
      width: '100%',
      backgroundColor: isSelected ? $theme.colors.backgroundTertiary : 'transparent',
      borderRadius: isSelected ? $theme.borders.radius300 : 'none',
      transitionProperty: 'background-color, border',
      transitionDuration: '0.2s',
      transitionTimingFunction: 'ease-in',
      ':active': {
        backgroundColor: $theme.colors.backgroundSecondary,
        borderRadius: $theme.borders.radius300
      }
    })
  },
  ArtworkContainer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }): StyleObject => ({
      minHeight: 0,
      width: 'auto',
      paddingRight: $theme.sizing.scale500
    })
  },
  Content: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }): StyleObject => ({
      minHeight: 0,
      padding: 0,
      gap: $theme.sizing.scale200
    })
  },
  EndEnhancerContainer: {
    style: {
      padding: 0
    }
  }
});
export const LabelItem = ({
  name,
  shortcutKey,
  annotationCount,
  isSelected,
  endEnhancer,
  nameEnhancer,
  onClick,
  onCountClick,
  handleLabelColorChange,
  renderIcon,
  color,
  getListItemOverrides,
  getTagOverrides,
  isM4Enabled = false,
  hideCountTag = false
}: PropsT) => {
  const [css, theme] = useStyletron();
  const handleClick = () => {
    onClick(name);
  };
  const hasAnnotations = annotationCount !== undefined && annotationCount > 0;
  const displayAnnotationCount = annotationCount ?? 0;
  const shouldShowCountTag = !hideCountTag && (isM4Enabled || hasAnnotations);
  const shortcutTag = shortcutKey ? <Tag size={SIZE.xSmall} hierarchy={isSelected ? HIERARCHY.primary : HIERARCHY.secondary} closeable={false} title={`Press ${shortcutKey} to select this label`}>
      {shortcutKey}
    </Tag> : null;
  const renderLeadingArtwork = () => {
    if (!isM4Enabled) {
      return <LabelColorPicker handleLabelColorChange={handleLabelColorChange} renderIcon={renderIcon} color={color} title={name} />;
    }
    return <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: theme.sizing.scale300
    })}>
        {shortcutTag}
        <LabelColorPicker handleLabelColorChange={handleLabelColorChange} renderIcon={renderIcon} color={color} title={name} />
      </div>;
  };
  return <ListItem artwork={renderLeadingArtwork} onClick={handleClick} overrides={{
    ...getDefaultListItemOverrides(isSelected),
    ...getListItemOverrides?.(isSelected)
  }} endEnhancer={() => <div className={css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.sizing.scale300
  })}>
          {!isM4Enabled && shortcutTag}
          {shouldShowCountTag ? <Tag hierarchy={isSelected ? HIERARCHY.primary : HIERARCHY.secondary} startEnhancer={() => <ChevronRightSmall size={16} />} size={SIZE.xSmall} closeable={false} title={`View ${displayAnnotationCount} annotation${displayAnnotationCount === 1 ? '' : 's'}`} overrides={{
      ...getAnnotationCountTagOverrides(),
      ...getTagOverrides?.(isSelected)
    }} onClick={event => {
      event.stopPropagation();
      onCountClick(name);
    }}>
              {displayAnnotationCount}
            </Tag> : null}
          {endEnhancer}
        </div>} hasDivider={false}>
      <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: theme.sizing.scale300
    })}>
        <ParagraphMedium margin={0} className={css({
        wordBreak: 'break-all'
      })}>
          {name}
        </ParagraphMedium>
        {nameEnhancer}
      </div>
    </ListItem>;
};